const normalizeState = (value) => String(value ?? '').trim().toUpperCase();

export const humanizeState = (value, fallback = 'N/A') => {
  const normalized = normalizeState(value);
  return normalized ? normalized.replaceAll('_', ' ') : fallback;
};

export const getRiskStateMeta = (value) => {
  const state = normalizeState(value);
  if (state === 'NORMAL') return { label: 'NORMAL', color: 'success' };
  if (state === 'BAD_DAY_ARMED') return { label: 'BAD DAY ARMED', color: 'warning' };
  if (!state) return { label: 'N/A', color: 'secondary' };
  return { label: humanizeState(state), color: 'warning' };
};

export const getCircuitStatusMeta = (value) => {
  const status = normalizeState(value);
  if (status === 'ACTIVE') return { label: 'ACTIVE', color: 'danger' };
  if (status === 'INACTIVE') return { label: 'INACTIVE', color: 'success' };
  if (!status) return { label: 'N/A', color: 'secondary' };
  return { label: humanizeState(status), color: 'warning' };
};

export const getDataStateMeta = (value) => {
  const state = String(value ?? '').trim().toLowerCase();
  if (state === 'valid') return { label: 'VALID', color: 'success' };
  if (state === 'stale') return { label: 'STALE', color: 'warning' };
  if (state === 'missing') return { label: 'MISSING DATA', color: 'secondary' };
  if (state === 'error') return { label: 'DATA ERROR', color: 'danger' };
  return { label: 'UNKNOWN', color: 'secondary' };
};

export const getOverallStateMeta = (account) => {
  const dataState = String(account?.data_state ?? '').toLowerCase();
  const status = normalizeState(account?.status);
  const riskState = normalizeState(account?.risk_state);

  if (dataState === 'error') return { label: 'DATA ERROR', color: 'danger' };
  if (dataState === 'missing') return { label: 'MISSING DATA', color: 'secondary' };
  if (dataState === 'stale') return { label: 'STALE', color: 'warning' };
  if (account?.lock_for_day === true) return { label: 'LOCKED FOR DAY', color: 'danger' };
  if (status === 'ACTIVE') return { label: 'BREAKER ACTIVE', color: 'danger' };
  if (riskState === 'BAD_DAY_ARMED') return { label: 'BAD DAY ARMED', color: 'warning' };
  if (riskState === 'NORMAL' && status === 'INACTIVE') {
    return { label: 'NORMAL', color: 'success' };
  }
  return { label: 'UNKNOWN', color: 'warning' };
};

export const getExecutionMeta = (value, trueLabel, falseLabel) => {
  if (value === true) return { label: trueLabel, color: 'danger' };
  if (value === false) return { label: falseLabel, color: 'success' };
  return { label: 'N/A', color: 'secondary' };
};

export const formatNullableNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? numeric.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : 'N/A';
};

export const formatRatioPercent = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${(numeric * 100).toFixed(2)}%` : 'N/A';
};

export const getLossExposure = (totalPnl, hardLossThreshold) => {
  if ([totalPnl, hardLossThreshold].some((value) => value === null || value === undefined || value === '')) return null;
  const total = Number(totalPnl);
  const hardLoss = Number(hardLossThreshold);
  if (!Number.isFinite(total) || !Number.isFinite(hardLoss) || hardLoss >= 0) return null;

  return Math.min(100, Math.max(0, (Math.abs(Math.min(total, 0)) / Math.abs(hardLoss)) * 100));
};

export const buildCircuitBreakerViewModel = (account) => ({
  overall: getOverallStateMeta(account),
  riskState: getRiskStateMeta(account?.risk_state),
  circuitStatus: getCircuitStatusMeta(account?.status),
  dataState: getDataStateMeta(account?.data_state),
  newOrders: getExecutionMeta(account?.should_block_new_orders, 'BLOCKED', 'ALLOWED'),
  closeAccount: getExecutionMeta(account?.should_close_account, 'REQUESTED', 'NO'),
  dailyLock: getExecutionMeta(account?.lock_for_day, 'ACTIVE', 'NO'),
});
